
/**
 * PayPal Subscription Service
 */
import { supabase } from "../integrations/supabase/client";

// Get plan ID based on tier and billing cycle
export function getPaypalPlanIdForTier(tier: string, billingCycle: 'monthly' | 'annual' = 'monthly'): string {
  // Live PayPal plan IDs from PayPal Developer Dashboard (Production)
  const planIds = {
    standard: {
      monthly: "P-8BE848314P5545947NA4QGLA", // Standard Plan Monthly (Live)
      annual: "P-2F058823RV403460GNA4QIIA"   // Standard Plan Annual (Live)
    },
    premium: {
      monthly: "P-9BE33493M0993604PNA4QJCA", // Premium Plan Monthly (Live)
      annual: "P-6GY09720YW3823642NA4QKKI"   // Premium Plan Annual (Live)
    },
    ultimate: {
      monthly: "P-6PF883614H791722KNA4QLEY", // Ultimate Plan Monthly (Live)
      annual: "P-517472277V054493SNA4QQVQ"   // Ultimate Plan Annual (Live)
    },
    provider: {
      annual: "P-04B80992XX8382934NEBLHQY"   // Provider Plan Annual ($299/year + $10 setup fee)
    }
  };

  const planId = planIds[tier as keyof typeof planIds]?.[billingCycle] || planIds.standard.monthly;

  console.log(`Getting plan ID for tier: ${tier}, billing: ${billingCycle}, plan ID: ${planId}`);

  return planId;
}

// Create a PayPal subscription
export async function createPaypalSubscription(planId: string) {
  try {
    console.log("=== PayPal Subscription Creation Started ===");
    console.log("Creating PayPal subscription for plan:", planId);

    // Get current user and verify authentication
    console.log("Getting current user...");
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error("User authentication error:", userError);
      return { success: false, error: "User not authenticated. Please log in and try again." };
    }

    console.log("User authenticated:", user.id);

    // Get current session to ensure we have a valid token
    console.log("Getting current session...");
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("Session error:", sessionError);
      return { success: false, error: "No valid session. Please log in again." };
    }

    console.log("Session valid, access token length:", session.access_token?.length);

    // Call the Supabase Edge Function to create a PayPal subscription
    console.log("Calling Supabase edge function 'create-paypal-subscription'...");
    const { data, error } = await supabase.functions.invoke('create-paypal-subscription', {
      body: {
        planId,
        customId: user.id // Add user ID to link subscription to user
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    console.log("Edge function response - data:", data);
    console.log("Edge function response - error:", error);

    if (error) {
      console.error("Supabase function error:", error);

      // Check if it's a network connectivity issue
      if (error.message?.includes('Failed to fetch') || error.message?.includes('network')) {
        return {
          success: false,
          error: "Network connection issue. PayPal integration works on your live domain but may have restrictions in development. Please try on your actual domain."
        };
      }

      return {
        success: false,
        error: error.message || "Failed to create subscription. Please try again."
      };
    }

    if (!data) {
      console.error("No data returned from function");
      return {
        success: false,
        error: "No response from payment service. This may be a development environment limitation. Please try on your live domain."
      };
    }

    console.log("PayPal subscription created successfully:", data);

    if (!data.approve_url) {
      console.error("No approval URL in response:", data);
      return {
        success: false,
        error: "Payment service did not provide checkout URL. Please try again."
      };
    }

    console.log("=== PayPal Subscription Creation Completed Successfully ===");
    return {
      success: true,
      approve_url: data.approve_url,
      subscription_id: data.subscription_id
    };
  } catch (error: any) {
    console.error("Error in createPaypalSubscription:", error);

    // Check for specific error types
    if (error.name === 'TypeError' && error.message?.includes('fetch')) {
      return {
        success: false,
        error: "Connection failed. PayPal buttons work on your live domain but may not work in development environment due to domain restrictions."
      };
    }

    return {
      success: false,
      error: error.message || "An unexpected error occurred. Please try again or use your live domain."
    };
  }
}

// Cancel a PayPal subscription
export async function cancelPaypalSubscription(subscriptionId: string) {
  try {
    console.log("Cancelling PayPal subscription:", subscriptionId);

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("No active session for PayPal cancellation");
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase.functions.invoke('cancel-paypal-subscription', {
      body: { subscriptionId },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (error) {
      console.error("Error cancelling PayPal subscription:", error);
      throw new Error(error.message || "Failed to cancel subscription");
    }

    console.log("PayPal subscription cancelled successfully");
    return data;
  } catch (error: any) {
    console.error("Error in cancelPaypalSubscription:", error);
    throw error;
  }
}

// Get PayPal subscription status
export async function getPaypalSubscriptionStatus(subscriptionId: string) {
  try {
    console.log("Getting PayPal subscription status for:", subscriptionId);

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("No active session for PayPal status check");
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase.functions.invoke('get-paypal-subscription-status', {
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (error) {
      console.error("Error getting PayPal subscription status:", error);
      throw new Error(error.message || "Failed to get subscription status");
    }

    console.log("PayPal subscription status retrieved successfully");
    return data;
  } catch (error: any) {
    console.error("Error in getPaypalSubscriptionStatus:", error);
    throw error;
  }
}

// Revise/Change PayPal subscription plan
export async function revisePaypalSubscription(currentSubscriptionId: string, newPlanId: string) {
  try {
    console.log("Revising PayPal subscription:", currentSubscriptionId, "to plan:", newPlanId);

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error("No active session for PayPal subscription revision");
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase.functions.invoke('revise-paypal-subscription', {
      body: {
        currentSubscriptionId,
        newPlanId
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (error) {
      console.error("Error revising PayPal subscription:", error);
      throw new Error(error.message || "Failed to change subscription plan");
    }

    console.log("PayPal subscription revised successfully:", data);
    return data;
  } catch (error: any) {
    console.error("Error in revisePaypalSubscription:", error);
    throw error;
  }
}

// Define the PayPal subscription details type
export interface PayPalSubscriptionDetails {
  id: string;
  status: string;
  start_time: string;
  next_billing_time?: string;
  last_payment?: {
    amount: {
      value: string;
      currency_code: string;
    };
    time: string;
  };
  payer?: {
    email_address: string;
    name?: {
      given_name: string;
      surname: string;
    };
  };
}
